import classNames from "classnames";
import { Fragment, useRef, useState } from "react";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import { I18n } from "../../hooks/useTranslations";
import { Glyph } from "../../types";
import SvgDelete from "../icons/Delete";
import SvgDownload from "../icons/Download";
import SvgEdit from "../icons/Edit";
import SvgHistory from "../icons/History";
import SvgMoreHoriz from "../icons/MoreHoriz";
import SvgPreview from "../icons/Preview";
import Icon from "./Icon";
import styles from "./ListItemMenu.module.css";

// type XOR<T, U> = (T & { [K in keyof U]?: never }) | (U & { [K in keyof T]?: never });

export type Action = {
  onClick: () => void;
  isDisabled?: boolean;
  title: string;
  isDivider?: boolean;
  icon?: Glyph;
  color?: "red" | "blue" | "green";
  visible?: boolean;
};

export const getDeleteAction = (i18n: I18n, onClick: () => void, isDivider = false, isDisabled?: boolean): Action => ({
  onClick,
  title: i18n.t.common.delete,
  color: "red",
  icon: SvgDelete,
  isDivider,
  isDisabled,
});

export const getDownloadAction = (i18n: I18n, onClick: () => void, isDivider = false): Action => ({
  onClick,
  title: i18n.t.common.download,
  icon: SvgDownload,
  isDivider,
});

export const getPreviewAction = (i18n: I18n, onClick: () => void, isDivider = false): Action => ({
  onClick,
  title: i18n.t.common.preview,
  icon: SvgPreview,
  isDivider,
});

export const getEditAction = (i18n: I18n, onClick: () => void, isDivider = false, isDisabled?: boolean): Action => ({
  onClick,
  title: i18n.t.common.edit,
  icon: SvgEdit,
  isDivider,
  isDisabled,
});

export const getHistoryAction = (i18n: I18n, onClick: () => void, isDivider = false): Action => ({
  onClick,
  title: i18n.t.common.history,
  icon: SvgHistory,
  isDivider,
});

interface ListItemMenuProps<T> {
  actions: Action[];
  item: T;
}

const ListItemMenu = <T,>({ actions }: ListItemMenuProps<T>) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ left: number; top: number } | null>(null);

  const menuRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(menuRef, () => setModalOpen(false));

  const handleIconClick = (e: React.MouseEvent<Element, MouseEvent>) => {
    e.stopPropagation();

    const { clientX, clientY } = e;

    setModalOpen(true);

    requestAnimationFrame(() => {
      if (menuRef.current) {
        const menuWidth = menuRef.current.offsetWidth;
        const menuHeight = menuRef.current.offsetHeight;
        const margin = 8;

        // Fixed-position menus must be positioned in viewport coordinates.
        let newLeft = clientX;
        let newTop = clientY;

        if (window.innerWidth - clientX < menuWidth + margin) {
          newLeft = clientX - menuWidth;
        }

        if (window.innerHeight - clientY < menuHeight + margin) {
          newTop = clientY - menuHeight;
        }

        newLeft = Math.max(margin, Math.min(newLeft, window.innerWidth - menuWidth - margin));
        newTop = Math.max(margin, Math.min(newTop, window.innerHeight - menuHeight - margin));

        setMenuPosition({ left: newLeft, top: newTop });
      }
    });
  };

  return (
    <div
      ref={containerRef}
      className={styles.menuContainer}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <div onClick={handleIconClick} className={styles.menuButton}>
        <Icon glyph={SvgMoreHoriz} className={styles.triggerIcon} />
      </div>
      <div
        className={classNames(styles.menu, isModalOpen ? styles.visible : undefined)}
        ref={menuRef}
        style={{
          left: `${menuPosition?.left}px`,
          top: `${menuPosition?.top}px`,
        }}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        <ul className={styles.list}>
          {actions.map((action, index) => {
            const isDisabled = action.isDisabled;
            const isVisible = action.visible ?? true;
            if (!isVisible) return null;

            return (
              <Fragment key={index}>
                {action.isDivider && <div className={styles.divider} />}

                <li
                  className={classNames(styles.listItem, {
                    [styles.disabled]: isDisabled,
                    [styles.green]: action.color === "green",
                    [styles.red]: action.color === "red",
                    [styles.blue]: action.color === "blue",
                  })}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isDisabled) return;
                    setModalOpen(false);
                    action.onClick();
                  }}
                >
                  {action.icon && <Icon glyph={action.icon} className={styles.itemIcon} />}
                  <span>{action.title}</span>
                </li>
              </Fragment>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default ListItemMenu;
